import SEO from '../components/SEO';
import { eventResultsSchema, organizationSchema, breadcrumbSchema } from '../utils/structuredData';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, TrendingUp, DollarSign, Users, Calendar, ArrowRight, Award } from 'lucide-react';
import { eventResults } from '../data/results';

const EventResultsPage = () => {
  const [filter, setFilter] = useState('all');
  
  const filteredResults = filter === 'all' 
    ? eventResults 
    : eventResults.filter(r => r.category === filter);

  return (
    <div style={styles.page}>
      <SEO
        title="Dealership Event Results — Real Numbers Nationwide"
        description="Documented results from 6,500+ dealership events — real appointments, units sold, and gross profit for car, golf cart, motorcycle, ATV & RV dealers."
        keywords="dealership event results, automotive event marketing results, car dealer event numbers, units sold dealer events, gross profit dealer promotion, event marketing ROI"
        canonicalUrl="https://driven2win.com/event-results"
        structuredData={[eventResultsSchema, organizationSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'Results',url:'https://driven2win.com/event-results'}])]}
      />
      <style>{`
        @media (max-width: 768px) {
          .results-hero-h1 { font-size: 32px !important; letter-spacing: 1px !important; }
          .results-hero-sub { font-size: 15px !important; }
          .results-hero-section { padding: 60px 0 !important; }
          .results-section-h2 { font-size: 26px !important; letter-spacing: 1px !important; margin-bottom: 36px !important; }
          .results-cta-h2 { font-size: 26px !important; }
          .results-cta-text { font-size: 16px !important; }
          .results-cta-btn { font-size: 18px !important; padding: 16px 20px !important; }
          .results-grid { grid-template-columns: 1fr !important; }
          .results-summary-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; }
          .results-summary-num { font-size: 40px !important; }
          .results-section-pad { padding: 60px 0 !important; }
          .results-card { padding: 20px !important; }
          .results-stat-num { font-size: 26px !important; }
          .results-filter-wrap { gap: 10px !important; }
          /* Result card stat numbers + labels */
          .rs-stat-num { font-size: 20px !important; }
          .rs-stat-label { font-size: 9px !important; letter-spacing: 0.3px !important; }
        }
      `}</style>
      {/* Hero */}
      <section style={styles.hero} className="results-hero-section">
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <h1 style={styles.heroH1} className="results-hero-h1">
            PROVEN <span style={{color: '#FFC300'}}>RESULTS</span>
          </h1>
          <p style={styles.heroSubheadline} className="results-hero-sub">
            These are REAL numbers from REAL dealers across America. No guesswork. No hype. Just documented event results that speak for themselves.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section style={styles.filterSection}>
        <div style={styles.container}>
          <div style={styles.filterButtons}>
            <button 
              onClick={() => setFilter('all')}
              style={{...styles.filterBtn, ...(filter === 'all' ? styles.filterBtnActive : {})}}
            >
              All Events
            </button>
            <button 
              onClick={() => setFilter('automotive')}
              style={{...styles.filterBtn, ...(filter === 'automotive' ? styles.filterBtnActive : {})}}
            >
              Car & Truck
            </button>
            <button 
              onClick={() => setFilter('golf-cart')}
              style={{...styles.filterBtn, ...(filter === 'golf-cart' ? styles.filterBtnActive : {})}}
            >
              Golf Cart
            </button>
            <button 
              onClick={() => setFilter('motorcycle')}
              style={{...styles.filterBtn, ...(filter === 'motorcycle' ? styles.filterBtnActive : {})}}
            >
              Motorcycle
            </button>
            <button 
              onClick={() => setFilter('rv')}
              style={{...styles.filterBtn, ...(filter === 'rv' ? styles.filterBtnActive : {})}}
            >
              RV
            </button>
          </div>
        </div>
      </section>

      {/* Results Grid */}
      <section style={styles.resultsSection} className="results-section-pad">
        <div style={styles.container}>
          <div className="grid-3col" style={styles.resultsGrid}>
            {filteredResults.map((result, index) => (
              <ResultCard key={index} {...result} />
            ))}
          </div>
        </div>
      </section>

      {/* Summary Stats */}
      <section style={styles.summarySection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="results-section-h2">
            AGGREGATE <span style={{color: '#FFC300'}}>PERFORMANCE</span>
          </h2>
          
          <div style={styles.summaryGrid}>
            <SummaryStat
              icon={<Calendar />}
              number="6,500+"
              label="Total Events Produced"
              color="#E31E24"
            />
            <SummaryStat
              icon={<DollarSign />}
              number="$250K+"
              label="Average Event Gross"
              color="#FF6B35"
            />
            <SummaryStat
              icon={<TrendingUp />}
              number="85+"
              label="Average Units Sold"
              color="#0080FF"
            />
            <SummaryStat
              icon={<Users />}
              number="125+"
              label="Average Appointments"
              color="#FFC300"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <h2 style={styles.ctaH2}>
            Ready to Add <span style={{color: '#FFC300'}}>YOUR SUCCESS STORY</span> to This List?
          </h2>
          <p style={styles.ctaText}>
            Let's create an event that delivers MASSIVE results for your dealership!
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary}>
            Schedule Your FREE Event Strategy Call
            <ArrowRight size={24} style={{marginLeft: '12px'}} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Components
const ResultCard = ({ location, state, category, eventType, duration, appointments, units, gross, testimonial, dealer }) => (
  <div style={styles.resultCard}>
    <div style={styles.resultHeader}>
      <div style={styles.resultLocation}>
        <MapPin size={20} color="#FFC300" />
        <span>{location}, {state}</span>
      </div>
      <div style={styles.resultCategory}>{categoryLabels[category]}</div>
    </div>
    
    <div style={styles.resultDetails}>
      <div style={styles.resultDetail}>
        <div style={styles.resultLabel}>Event Type</div>
        <div style={styles.resultValue}>{eventType}</div>
      </div>
      <div style={styles.resultDetail}>
        <div style={styles.resultLabel}>Duration</div>
        <div style={styles.resultValue}>{duration}</div>
      </div>
    </div>
    
    <div style={styles.resultStats}>
      <div style={styles.resultStat}>
        <div style={styles.resultStatNumber} className="rs-stat-num">{appointments}</div>
        <div style={styles.resultStatLabel} className="rs-stat-label">Appointments</div>
      </div>
      <div style={styles.resultStat}>
        <div style={styles.resultStatNumber} className="rs-stat-num">{units}</div>
        <div style={styles.resultStatLabel} className="rs-stat-label">Units Sold</div>
      </div>
      <div style={styles.resultStat}>
        <div style={styles.resultStatNumber} className="rs-stat-num">${(gross/1000).toFixed(0)}K</div>
        <div style={styles.resultStatLabel} className="rs-stat-label">Gross Profit</div>
      </div>
    </div>
    
    {testimonial && (
      <div style={styles.resultTestimonial}>
        <div style={styles.testimonialQuote}>"{testimonial}"</div>
        <div style={styles.testimonialAuthor}>— {dealer}</div>
      </div>
    )}
  </div>
);

const SummaryStat = ({ icon, number, label, color }) => (
  <div style={styles.summaryStatCard}>
    <div style={{...styles.summaryStatIcon, background: color}}>
      {React.cloneElement(icon, { size: 48, color: 'white' })}
    </div>
    <div style={styles.summaryStatNumber}>{number}</div>
    <div style={styles.summaryStatLabel}>{label}</div>
  </div>
);

// Data
const categoryLabels = {
  'automotive': 'Car & Truck',
  'golf-cart': 'Golf Cart',
  'motorcycle': 'Motorcycle',
  'rv': 'RV'
};

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #1A1F3A 0%, #0F1118 100%)',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(227, 30, 36, 0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    maxWidth: '900px',
    margin: '0 auto',
    lineHeight: '1.7',
    position: 'relative',
    zIndex: 1,
  },
  filterSection: {
    padding: '40px 0',
    background: '#F5F5F5',
    borderBottom: '3px solid #E5E5E5',
  },
  filterButtons: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  filterBtn: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '18px',
    padding: '12px 32px',
    border: '2px solid #CCC',
    background: '#FFFFFF',
    color: '#666',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
  filterBtnActive: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: '#FFFFFF',
    border: '2px solid transparent',
  },
  resultsSection: {
    padding: '80px 0',
    background: '#FFFFFF',
  },
  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))',
    gap: '32px',
  },
  resultCard: {
    background: '#FFFFFF',
    border: '3px solid #E5E5E5',
    borderRadius: '16px',
    padding: '32px',
    transition: 'all 0.3s ease',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '2px solid #F5F5F5',
  },
  resultLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    color: '#1A1F3A',
    letterSpacing: '1px',
  },
  resultCategory: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '12px',
    color: '#FFFFFF',
    background: '#0080FF',
    padding: '6px 12px',
    borderRadius: '4px',
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  resultDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  resultDetail: {},
  resultLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '12px',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  resultValue: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    color: '#333',
    fontWeight: '700',
  },
  resultStats: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '24px 0',
    borderTop: '2px solid #F5F5F5',
    borderBottom: '2px solid #F5F5F5',
    marginBottom: '24px',
  },
  resultStat: {
    textAlign: 'center',
  },
  resultStatNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '36px',
    fontWeight: '700',
    color: '#E31E24',
    marginBottom: '4px',
  },
  resultStatLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '11px',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '600',
  },
  resultTestimonial: {
    background: '#F5F5F5',
    borderRadius: '12px',
    padding: '20px',
  },
  testimonialQuote: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#333',
    fontStyle: 'italic',
    marginBottom: '8px',
  },
  testimonialAuthor: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '12px',
    color: '#666',
    fontWeight: '700',
  },
  summarySection: {
    padding: '100px 0',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
  },
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: '64px',
    letterSpacing: '2px',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
    gap: '32px',
  },
  summaryStatCard: {
    background: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '16px',
    padding: '40px 32px',
    textAlign: 'center',
  },
  summaryStatIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  summaryStatNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '56px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '8px',
  },
  summaryStatLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  ctaSection: {
    background: '#1A1F3A',
    padding: '100px 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    marginBottom: '48px',
    fontWeight: '600',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px 48px',
    border: 'none',
    borderRadius: '8px',
    textDecoration: 'none',
    boxShadow: '0 6px 24px rgba(227, 30, 36, 0.4)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default EventResultsPage;
